import type { Assignment, Job } from '@/types'

export interface PanelAssignment {
  assignmentId: string
  workerId: string
  name: string
  statusLabel: string
  canRemove: boolean
  /** Why Remove is disabled (shown next to the worker), null when it can be removed. */
  removeBlockedReason: string | null
}

/** Assignments that no longer hold a worker on the job (same as the Real Time page). */
const INACTIVE_ASSIGNMENT_STATUSES = new Set(['withdrawn', 'cancelled'])
/** The backend only deletes assignments that are still pending / assigned. */
const REMOVABLE_ASSIGNMENT_STATUSES = new Set(['pending', 'assigned'])

const STATUS_LABEL: Record<string, string> = {
  pending: 'Pending',
  assigned: 'Assigned',
  accepted: 'Accepted',
  rejected: 'Declined',
  auto_rejected: 'Expired',
  completed: 'Completed',
}

const labelOf = (status: string) => STATUS_LABEL[status] ?? (status ? status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ') : 'Pending')

function workerIdOf(assignment: Assignment): string | null {
  if (assignment.worker_id != null && String(assignment.worker_id).trim()) return String(assignment.worker_id)
  const { worker } = assignment
  if (worker == null) return null
  if (typeof worker === 'object') return String(worker.worker_id ?? worker.id)
  return String(worker)
}

/** Workers currently on the job, with whether Remove is allowed. */
export function resolvePanelAssignments(assignments: Assignment[]): PanelAssignment[] {
  return assignments.flatMap((assignment) => {
    const workerId = workerIdOf(assignment)
    const status = String(assignment.status || 'pending')
    if (!workerId || INACTIVE_ASSIGNMENT_STATUSES.has(status)) return []
    const statusLabel = labelOf(status)
    const canRemove = REMOVABLE_ASSIGNMENT_STATUSES.has(status)
    return [{
      assignmentId: String(assignment.id),
      workerId,
      name: String(assignment.worker_name || '').trim() || `Worker #${workerId}`,
      statusLabel,
      canRemove,
      removeBlockedReason: canRemove ? null : `${statusLabel}, can't be removed here`,
    }]
  })
}

/** Cancelled / completed / locked jobs cannot change staffing (the backend refuses). */
export function resolveReadOnlyReason(job: Pick<Job, 'status' | 'is_locked'> | null | undefined): string | null {
  if (!job) return null
  if (job.status === 'cancelled') return 'Cancelled job · read only'
  if (job.status === 'completed') return 'Completed job · read only'
  if (job.is_locked) return 'Locked job · read only'
  return null
}
